import React, { useContext, memo } from 'react'
import { CssBaseline, Box } from '@mui/material'
import Pusher from 'pusher-js'
import { DialogPopup } from '../components'
import { FeedbackContext } from '../feedback'
import { translate } from '../i18n'
import { LayoutContext } from '../layout'
import { WebSocketPayload } from '../model'
import { AppContext } from './AppContext'
import InternAppBar from './InternAppBar'
import { InternDrawer } from './InternDrawer'
import { InternLayoutProps } from './InternLayout.types'
import { InternSurface } from './InternSurface'

function InternLayout({ routes }: InternLayoutProps) {
    const context = useContext(AppContext)
    const feedbackContext = useContext(FeedbackContext)
    const layoutContext = useContext(LayoutContext)

    const pusher = (() => {
        if (!context.session?.websocket) return undefined

        const websocket = context.session.websocket
        if (websocket.name !== 'pusher') return undefined

        Pusher.logToConsole = websocket.verbose

        const instance = new Pusher(websocket.key, {
            cluster: websocket.cluster,
            forceTLS: websocket.encrypted,
        })

        const channel = instance.subscribe(context.session!.secret_id)
        channel.bind(websocket.event_name, (payload: WebSocketPayload) => {
            if (payload.message.type === 'BLOCKED_USER') {
                layoutContext.blockUser()
            } else {
                feedbackContext.showInfo(payload.message.message)
            }
        })

        return instance
    })()

    const onSignOut = () => {
        if (pusher) pusher.disconnect()

        context.removeSession()
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            <InternAppBar
                email={context.session?.user.email ?? ''}
                onSignOut={onSignOut}
            />

            <InternDrawer routes={routes} />

            <InternSurface routes={routes} />

            <DialogPopup
                open={layoutContext.user.blocked}
                title={translate('users.blocked.title')}
                onClose={onSignOut}
                closeText={translate('buttons.signout') as string}
            >
                {translate('users.blocked.description')}
            </DialogPopup>
        </Box>
    )
}

export default memo(InternLayout)
